import { BaseRequest } from '@trukt/types/BaseRequest';
import { IntegrationApp, IntegrationAppConfig, IntegrationCompany } from '@trukt/types/Integration';
import { PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { SimplePaginatedResults } from '@trukt/types/PaginatedResults';
import { AxiosInstance } from 'axios';

export type GetIntegrationAppRequest = BaseRequest<
  { domain?: string[] | null } & Omit<PaginatedQueryParams, 'order'>
>;
export type GetIntegrationAppResponse = SimplePaginatedResults<IntegrationApp>;

export type UpdateIntegrationAppRequest = BaseRequest<void, { enabled: boolean }, { id: string }>;
export type UpdateIntegrationAppResponse = void;

export type GetIntegrationCompanyRequest = BaseRequest<
  { apps?: string[] | null; companies?: string[] } & Pick<PaginatedQueryParams, 'pg' | 'rc'>
>;
export type GetIntegrationCompanyResponse = SimplePaginatedResults<IntegrationCompany>;

export type GetIntegrationRecordTypeRequest = BaseRequest<Pick<PaginatedQueryParams, 'pg' | 'rc'>>;
export type GetIntegrationRecordTypeResponse = SimplePaginatedResults<{ id: number; type: string }>;

export type PostIntegrationCompanyRequest = BaseRequest<
  void,
  { externalId: string; recordType: string },
  { appId: string; companyGuid: string }
>;
export type PostIntegrationCompanyResponse = void;
export type PutIntegrationCompanyRequest = BaseRequest<
  void,
  { externalId?: string; recordType?: string },
  { companyGuid: string }
>;
export type PutIntegrationCompanyResponse = void;

export type GetIntegrationConfigListRequest = BaseRequest<void, void, { appId: string }>;
export type GetIntegrationConfigListResponse = IntegrationAppConfig[];

export type UpdateIntegrationConfig = Pick<IntegrationAppConfig, 'id'> &
  Partial<Omit<IntegrationAppConfig, 'id'>>;
export type UpdateIntegrationConfigRequest = BaseRequest<
  void,
  UpdateIntegrationConfig[],
  { appId: string }
>;
export type UpdateIntegrationConfigResponse = void;

export class IntegrationService {
  instance: AxiosInstance;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  getAppList({ queryParams }: GetIntegrationAppRequest) {
    return this.instance.get<GetIntegrationAppResponse>('/integrations/apps', {
      params: queryParams,
    });
  }

  updateApp({ routeParams, body }: UpdateIntegrationAppRequest) {
    return this.instance.put<UpdateIntegrationAppResponse>(
      `/integrations/apps/${routeParams.id}`,
      body,
    );
  }

  getCompanyList({ queryParams }: GetIntegrationCompanyRequest) {
    return this.instance.get<GetIntegrationCompanyResponse>('/integrations/companies', {
      params: queryParams,
    });
  }

  getRecordTypeList({ queryParams }: GetIntegrationRecordTypeRequest) {
    return this.instance.get<GetIntegrationRecordTypeResponse>('/integrations/record-types', {
      params: queryParams,
    });
  }

  postCompany({ routeParams, body }: PostIntegrationCompanyRequest) {
    return this.instance.post<PostIntegrationCompanyResponse>(
      `/integrations/companies/${routeParams.companyGuid}/apps/${routeParams.appId}`,
      body,
    );
  }

  updateCompany({ routeParams, body }: PutIntegrationCompanyRequest) {
    return this.instance.put<PutIntegrationCompanyResponse>(
      `/integrations/companies/${routeParams.companyGuid}`,
      body,
    );
  }

  getAppConfigs({ routeParams }: GetIntegrationConfigListRequest) {
    return this.instance.get<GetIntegrationConfigListResponse>(
      `/integrations/${routeParams.appId}/configs`,
    );
  }

  updateAppConfig({ routeParams, body }: UpdateIntegrationConfigRequest) {
    return this.instance.put<UpdateIntegrationConfigResponse>(
      `/integrations/${routeParams.appId}/configs`,
      body,
    );
  }
}
